import React from 'react';
import { Card } from '@thd-olt-component-react/card';
import {
  extend,
  params,
  arrayOf as arrayType,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import PropTypes, { node, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { VideoPlayer } from '@thd-olt-component-react/video-player';
import { Row, Col } from '@thd-olt-component-react/grid';
import { EducationalInfo } from './EducationalInfo/EducationalInfo';
import { AdditionalDetails } from './AdditionalDetails/AdditionalDetails';
import './educational-summary.style.scss';

const EducationalSummary = ({
  itemId, title, maxVideos, showDetails, children
}) => {

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });

  if (loading || (error && !data) || !data?.product) {
    return null;
  }
  const itemTitle = data?.product?.identifiers?.productLabel;

  const {
    video
  } = data?.product?.media || [];

  if (!video) {
    return null;
  }

  const getSupplementVideos = (videos) => {
    return videos.filter((eachVideo) => eachVideo.type.match(/SUPPLEMENT/ig));
  };

  const supplementalVideos = maxVideos ? getSupplementVideos(video).slice(0, maxVideos) : getSupplementVideos(video);

  if (!supplementalVideos.length) { return null; }

  const multipleVideos = supplementalVideos.length > 1;
  const detailsBreakpoint = showDetails ? '12' : '6';
  const detailsWrapperBreakpoint = showDetails ? '6' : '12';
  const videoAndDetailsBreakpoint = ((multipleVideos && showDetails) || showDetails) ? '6' : '12';
  const multVideoWrapperBreakpoint = multipleVideos ? detailsWrapperBreakpoint : '12';

  const videoWrapClasses = classNames({
    'educational-summary__desktop-view--container': !multipleVideos,
    'educational-summary__desktop-view--container--wrap-sooner': multipleVideos
  });

  const itemWrapperClasses = classNames('educational-summary__item-wrapper', {
    'educational-summary__item-wrapper--details': showDetails
  });

  return (
    <div className="educational-summary">
      <Card noHide className="educational-summary__desktop-view--wrapper">
        <Card.Content maxHeight="none" title={title || itemTitle} underline>
          <Row>
            <Col nopadding className="educational-summary__desktop-view">
              <Col
                xs={multVideoWrapperBreakpoint}
                sm={videoAndDetailsBreakpoint}
                className={videoWrapClasses}
                nopadding
              >
                {
                  supplementalVideos.map((supVideo, i) => {
                    return (
                      <div className={itemWrapperClasses} key={i}>
                        <Col xs={detailsBreakpoint} nopadding className="educational-summary__video">
                          <VideoPlayer
                            videoStill={supVideo.videoStill}
                            videoId={supVideo.videoId}
                          />
                        </Col>
                        <Col xs={detailsBreakpoint} nopadding>
                          <EducationalInfo
                            title={supVideo.title}
                            shortDescription={supVideo.shortDescription}
                            link={supVideo.link}
                            multiView={multipleVideos || showDetails}
                          />
                        </Col>
                      </div>
                    );
                  })
                }
              </Col>
              {
                showDetails && (
                  <Col nopadding xs={multVideoWrapperBreakpoint} sm={videoAndDetailsBreakpoint}>
                    <div className="educational-summary__details--container">
                      <AdditionalDetails itemId={itemId}>
                        {children}
                      </AdditionalDetails>
                    </div>
                  </Col>
                )
              }
            </Col>
          </Row>
        </Card.Content>
      </Card>

    </div>
  );
};

EducationalSummary.displayName = 'EducationalSummary';

EducationalSummary.dataModel = extend(
  {
    product: params({ itemId: stringType().isRequired() }).shape({
      dataSources: stringType(),
      identifiers: shapeType({
        productLabel: stringType()
      }),
      media: shapeType({
        video: arrayType(shapeType({
          link: shapeType({
            text: stringType(),
            url: stringType()
          }),
          shortDescription: stringType(),
          title: stringType(),
          type: stringType(),
          videoId: stringType(),
          videoStill: stringType()
        }))
      })
    })
  },
  AdditionalDetails
);

EducationalSummary.propTypes = {
  itemId: PropTypes.string.isRequired,
  title: PropTypes.string,
  maxVideos: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8]),
  showDetails: PropTypes.bool,
  children: PropTypes.oneOfType([node, arrayOf(node)])
};

EducationalSummary.defaultProps = {
  title: '',
  showDetails: false,
  children: null,
  maxVideos: null
};

export { EducationalSummary };
